import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('thm_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('thm_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Jobs API
export const jobsApi = {
  create: (data) => api.post('/jobs', data),
  getAll: (status) => api.get('/jobs', { params: { status } }),
  getAvailable: () => api.get('/jobs/available'),
  getById: (id) => api.get(`/jobs/${id}`),
  updateStatus: (id, status) => api.put(`/jobs/${id}/status`, { status }),
  // Enhanced delivery flow
  markPickedUp: (id) => api.post(`/jobs/${id}/picked-up`),
  markDelivered: (id, deliveryProof) => api.post(`/jobs/${id}/delivered`, deliveryProof),
  confirmDelivery: (id) => api.post(`/jobs/${id}/confirm-delivery`),
  disputeDelivery: (id, data) => api.post(`/jobs/${id}/dispute`, data),
};

// Quotes API
export const quotesApi = {
  create: (data) => api.post('/quotes', data),
  getByJob: (jobId) => api.get(`/quotes/job/${jobId}`),
  getMy: () => api.get('/quotes/my'),
  accept: (quoteId) => api.post(`/quotes/${quoteId}/accept`),
};

// Payments API
export const paymentsApi = {
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  release: (paymentId) => api.post(`/payments/${paymentId}/release`),
};

// Tracking API
export const trackingApi = {
  start: (jobId) => api.post(`/jobs/${jobId}/tracking/start`),
  stop: (jobId) => api.post(`/jobs/${jobId}/tracking/stop`),
  updateLocation: (jobId, location) => api.post(`/jobs/${jobId}/tracking/update`, location),
  getInfo: (jobId) => api.get(`/jobs/${jobId}/tracking`),
};

// Reviews API
export const reviewsApi = {
  create: (data) => api.post('/reviews', data),
  getByProvider: (providerId) => api.get(`/reviews/provider/${providerId}`),
};

// Providers API
export const providersApi = {
  getProfile: (id) => api.get(`/providers/${id}`),
};

// Stats API
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard'),
};

// Subscription API
export const subscriptionApi = {
  getMy: () => api.get('/subscription/my'),
  renew: (originUrl) => api.post('/subscription/renew', { origin_url: originUrl }),
  cancel: () => api.post('/subscription/cancel'),
  reactivate: () => api.post('/subscription/reactivate'),
  getCustomerPortal: () => api.post('/subscription/customer-portal'),
};

// Stripe Connect API
export const stripeConnectApi = {
  createAccount: (originUrl) => api.post('/stripe/connect/create-account', { origin_url: originUrl }),
  createOnboardingLink: (originUrl) => api.post('/stripe/connect/create-onboarding-link', { origin_url: originUrl }),
  getAccountStatus: () => api.get('/stripe/connect/account-status'),
  getDashboardLink: () => api.get('/stripe/connect/dashboard-link'),
};

// Stripe Setup Intent API (for card collection)
export const stripeApi = {
  createSetupIntent: () => api.post('/stripe/create-setup-intent'),
  confirmPaymentMethod: (paymentMethodId, setupIntentId) => 
    api.post('/stripe/confirm-payment-method', { payment_method_id: paymentMethodId, setup_intent_id: setupIntentId }),
  chargeSubscription: () => api.post('/stripe/charge-subscription'),
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
};

// Questions API
export const questionsApi = {
  getByJob: (jobId) => api.get(`/jobs/${jobId}/questions`),
  ask: (jobId, question) => api.post(`/jobs/${jobId}/questions`, { question }),
  answer: (jobId, questionId, answer) => api.post(`/jobs/${jobId}/questions/${questionId}/answer`, { answer }),
};

// Job Messages API (Private messaging after payment)
export const messagesApi = {
  getByJob: (jobId) => api.get(`/jobs/${jobId}/messages`),
  send: (jobId, message) => api.post(`/jobs/${jobId}/messages`, { message }),
  getUnreadCount: (jobId) => api.get(`/jobs/${jobId}/messages/unread-count`),
};

// Admin API
export const adminApi = {
  getPlatformFee: () => api.get('/admin/settings/platform-fee'),
  updatePlatformFee: (feePercent) => api.put('/admin/settings/platform-fee', { fee_percent: feePercent }),
  getPendingProviders: () => api.get('/admin/providers/pending'),
  getAllProviders: () => api.get('/admin/providers/all'),
  getProviderDetails: (providerId) => api.get(`/admin/provider/${providerId}`),
  verifyProvider: (providerId, action, notes) => api.post(`/admin/provider/${providerId}/verify`, { action, notes }),
  grantTrial: (providerId, trialDays = 14) => api.post(`/admin/provider/${providerId}/grant-trial?trial_days=${trialDays}`),
  revokeTrial: (providerId) => api.post(`/admin/provider/${providerId}/revoke-trial`),
  cancelSubscription: (providerId) => api.post(`/admin/provider/${providerId}/cancel-subscription`),
  deleteUser: (userId) => api.delete(`/admin/user/${userId}`),
  getProvidersWithTrials: () => api.get('/admin/providers/trials'),
  // Job management
  getAllJobs: (status = null, includeOld = false) => api.get('/admin/jobs', { params: { status, include_old: includeOld } }),
  deleteJob: (jobId) => api.delete(`/admin/job/${jobId}`),
  cancelJob: (jobId, reason = null) => api.post(`/admin/job/${jobId}/cancel`, { reason }),
  cleanupExpiredJobs: (daysOld = 30) => api.post(`/admin/jobs/cleanup-expired?days_old=${daysOld}`),
  // Content moderation
  getFlaggedContent: () => api.get('/admin/moderation/flagged'),
  approveQuestion: (questionId) => api.post(`/admin/moderation/question/${questionId}/approve`),
  approveAnswer: (questionId) => api.post(`/admin/moderation/answer/${questionId}/approve`),
  deleteQuestion: (questionId) => api.delete(`/admin/moderation/question/${questionId}`),
  deleteAnswer: (questionId) => api.delete(`/admin/moderation/answer/${questionId}`),
  // Database reset
  resetAllData: () => api.delete('/admin/reset-all-data'),
};

// Settings API (public)
export const settingsApi = {
  getPlatformFee: () => api.get('/settings/platform-fee'),
};

export default api;
